import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { getCachedAccessibleStores } from '@/lib/runtimeMetaCache';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Combobox } from '@/components/ui/combobox';
import { GroupOrderCombobox } from '@/components/ui/group-order-combobox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { CustomizationFlowModal } from '@/components/customizations/CustomizationFlowModal';
import { toast } from 'sonner';
import {
  getCustomers,
  getCustomersPaginated,
  createCustomer,
  getProducts,
  getProductWithCustomizations,
  createOrder,
  createPayment,
  getParentOrders,
} from '@/lib/api';
import CopyFromOrderDialog from '@/components/orders/CopyFromOrderDialog';
import { buildCopiedLineItems } from '@/lib/copyLineItems';
import { getApiBaseUrl } from '@/lib/apiBaseUrl';
import { isSessionExpiredError, triggerSessionExpired } from '@/lib/sessionRecovery';
import DateField from '@/components/common/DateField';
import { getFabricReferencePanel } from '@/lib/fabricReference';
import {
  ArrowLeft, ArrowRight, Check, User, Package, FileText,
  Plus, Trash2, Search, DollarSign, ShoppingCart, Copy, Users, ClipboardCopy, Shirt
} from 'lucide-react';

const steps = [
  { id: 1, name: 'Customer', icon: User },
  { id: 2, name: 'Products', icon: Package },
  { id: 3, name: 'Review', icon: FileText },
];

const API_URL = getApiBaseUrl();
const NEW_ORDER_DRAFT_KEY = 'new-order-draft-v1';

const NewOrderPage = () => {
  const { sessionModalOpen } = useAuth();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const submittingLockRef = useRef(false);

  // Stores list - fetched dynamically based on user access
  const [stores, setStores] = useState([]);
  const [factories, setFactories] = useState([]);

  // Customer state
  const [customers, setCustomers] = useState([]);
  const [customerSearch, setCustomerSearch] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [showNewCustomer, setShowNewCustomer] = useState(false);
  const [newCustomerForm, setNewCustomerForm] = useState({
    customer_name: '',
    email: '',
    phone_number: '',
    notes: '',
  });

  // Products state
  const [products, setProducts] = useState([]);
  const [testProducts, setTestProducts] = useState([]);
  const [lineItems, setLineItems] = useState([]);
  const [productDialogOpen, setProductDialogOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [productCustomizations, setProductCustomizations] = useState(null);
  const [customizationValues, setCustomizationValues] = useState({});
  const [lineItemQuantity, setLineItemQuantity] = useState(1);
  const [editingLineItem, setEditingLineItem] = useState(null); // For editing existing line items
  const [customBasePrice, setCustomBasePrice] = useState(null); // For overriding base price
  const [addCustomFieldDialogOpen, setAddCustomFieldDialogOpen] = useState(false);
  const [customFieldCategoryId, setCustomFieldCategoryId] = useState(null);
  const [customFieldForm, setCustomFieldForm] = useState({ field_name: '', field_value: '' });

  // Order details state
  const [orderDetails, setOrderDetails] = useState({
    client_final_date: '',
    internal_delivery_date: '',
    test_fit_date: '',
    store: '__none__',
    notes: '',
    gst_percentage: 0,
    discount_type: '__none__',
    discount_value: 0,
  });


  // Order type state (enquiry or order)
  const [orderType, setOrderType] = useState('order'); // 'enquiry' or 'order'

  // Payment state
  const [depositAmount, setDepositAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('__none__');

  // Group order state
  const [isGroupParent, setIsGroupParent] = useState(false);
  const [groupName, setGroupName] = useState('');
  const [parentOrders, setParentOrders] = useState([]);
  const [parentOrdersLoading, setParentOrdersLoading] = useState(false);
  const [selectedParentOrderId, setSelectedParentOrderId] = useState('__none__');

  // Copy from order state
  const [copyDialogOpen, setCopyDialogOpen] = useState(false);

  // Test Garment direct-add flow
  const [testGarmentDialogOpen, setTestGarmentDialogOpen] = useState(false);
  const [selectedTestProduct, setSelectedTestProduct] = useState('__none__');
  const [testGarmentFactorySelection, setTestGarmentFactorySelection] = useState('__none__');

  // Track if draft has been restored to avoid overwriting on initial render
  const draftRestoredRef = useRef(false);

  useEffect(() => {
    try {
      const savedDraft = localStorage.getItem(NEW_ORDER_DRAFT_KEY);
      if (!savedDraft) {
        draftRestoredRef.current = true; // No draft to restore, allow saving
        return;
      }
      const parsedDraft = JSON.parse(savedDraft);
      if (typeof parsedDraft?.currentStep === 'number') setCurrentStep(parsedDraft.currentStep);
      if (parsedDraft?.selectedCustomer) setSelectedCustomer(parsedDraft.selectedCustomer);
      if (typeof parsedDraft?.showNewCustomer === 'boolean') setShowNewCustomer(parsedDraft.showNewCustomer);
      if (parsedDraft?.newCustomerForm) setNewCustomerForm(parsedDraft.newCustomerForm);
      if (Array.isArray(parsedDraft?.lineItems)) setLineItems(parsedDraft.lineItems);
      if (parsedDraft?.orderDetails) setOrderDetails(parsedDraft.orderDetails);
      if (parsedDraft?.orderType) setOrderType(parsedDraft.orderType);
      if (typeof parsedDraft?.depositAmount === 'string') setDepositAmount(parsedDraft.depositAmount);
      if (parsedDraft?.paymentMethod) setPaymentMethod(parsedDraft.paymentMethod);
      if (typeof parsedDraft?.isGroupParent === 'boolean') setIsGroupParent(parsedDraft.isGroupParent);
      if (typeof parsedDraft?.groupName === 'string') setGroupName(parsedDraft.groupName);
      if (typeof parsedDraft?.selectedParentOrderId === 'string') setSelectedParentOrderId(parsedDraft.selectedParentOrderId);
      // Mark draft as restored after state updates are scheduled
      setTimeout(() => { draftRestoredRef.current = true; }, 0);
    } catch (error) {
      console.warn('Failed to restore new order draft', error);
      draftRestoredRef.current = true; // Allow saving even if restore failed
    }
  }, []);

  useEffect(() => {
    // Skip saving until draft has been restored to avoid overwriting with initial empty values
    if (!draftRestoredRef.current) return;
    const draftPayload = {
      currentStep,
      selectedCustomer,
      showNewCustomer,
      newCustomerForm,
      lineItems,
      orderDetails,
      orderType,
      depositAmount,
      paymentMethod,
      isGroupParent,
      groupName,
      selectedParentOrderId,
    };
    localStorage.setItem(NEW_ORDER_DRAFT_KEY, JSON.stringify(draftPayload));
  }, [currentStep, selectedCustomer, showNewCustomer, newCustomerForm, lineItems, orderDetails, orderType, depositAmount, paymentMethod, isGroupParent, groupName, selectedParentOrderId]);

  useEffect(() => {
    fetchCustomers();
    fetchProducts();
    fetchParentOrders();
    fetchStores();
    fetchFactories();
  }, []);

  const fetchFactories = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/factories?active_only=true`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) {
        throw new Error('Failed to fetch factories');
      }
      const factoriesData = await response.json();
      setFactories(Array.isArray(factoriesData) ? factoriesData : []);
    } catch (error) {
      console.error('Failed to fetch factories:', error);
      setFactories([]);
    }
  };

  const fetchStores = async () => {
    try {
      const storesData = await getCachedAccessibleStores();
      if (Array.isArray(storesData)) {
        setStores(storesData);
        
        // If user has only one store, auto-select it
        if (storesData.length === 1) {
          setOrderDetails(prev => ({ ...prev, store: storesData[0].store_id }));
        }
      } else {
        toast.error('Failed to load stores');
      }
    } catch (error) {
      console.error('Failed to fetch stores:', error);
      toast.error('Failed to load stores');
    }
  };

  const fetchCustomers = async () => {
    try {
      const aggregatedCustomers = [];
      let page = 1;
      let hasMore = true;
      const maxPages = 20;

      while (hasMore && page <= maxPages) {
        const response = await getCustomersPaginated({ page, page_size: 500 });
        const payload = response?.data || {};
        const batch = Array.isArray(payload.data) ? payload.data : [];

        aggregatedCustomers.push(...batch);
        hasMore = Boolean(payload.has_more) && batch.length > 0;
        page += 1;
      }

      setCustomers(aggregatedCustomers);
    } catch (error) {
      toast.error('Failed to fetch customers');
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await getProducts();
      const allProducts = response.data || [];
      setProducts(allProducts.filter(p => !p.name.startsWith('Test')));
      setTestProducts(allProducts.filter(p => p.name.startsWith('Test')));
    } catch (error) {
      toast.error('Failed to fetch products');
    }
  };

  const fetchParentOrders = useCallback(async (searchQuery = '') => {
    setParentOrdersLoading(true);
    try {
      const response = await getParentOrders(searchQuery, 120);
      setParentOrders(response.data);
    } catch (error) {
      console.error('Failed to fetch parent orders:', error);
    } finally {
      setParentOrdersLoading(false);
    }
  }, []);

  const handleCopyFromOrder = async (sourceLineItems, sourceOrder) => {
    const copiedItems = buildCopiedLineItems(sourceLineItems, { mode: 'create' });
    if (copiedItems.length === 0) {
      toast.error('No line items found in the selected order');
      return;
    }
    setLineItems((prev) => [...prev, ...copiedItems]);
    toast.success(`Copied ${copiedItems.length} item(s) from ${sourceOrder?.order_number || 'order'}`);
  };

  const handleCreateCustomer = async () => {
    try {
      const response = await createCustomer(newCustomerForm);
      setSelectedCustomer(response.data);
      setShowNewCustomer(false);
      setNewCustomerForm({ customer_name: '', email: '', phone_number: '', notes: '' });
      fetchCustomers();
      toast.success('Customer created');
    } catch (error) {
      if (isSessionExpiredError(error)) {
        triggerSessionExpired({ reason: 'Your session expired while creating a customer. Log in again to continue this order draft.' });
        return;
      }
      toast.error('Failed to create customer');
    }
  };

  const handleSelectProduct = async (product) => {
    setSelectedProduct(product);
    setLineItemQuantity(1);
    setLoading(true);
    try {
      const response = await getProductWithCustomizations(product.product_id);
      setProductCustomizations(response.data);
      // Initialize customization values with defaults
      const defaults = {};
      response.data.categories?.forEach(cat => {
        cat.options?.forEach(opt => {
          if (opt.default_value) {
            defaults[opt.option_id] = opt.default_value;
          }
        });
      });
      setCustomizationValues(defaults);
      setProductDialogOpen(true);
    } catch (error) {
      toast.error('Failed to load customizations');
    } finally {
      setLoading(false);
    }
  };

  const handleAddLineItem = () => {
    if (!selectedProduct) return;

    // Validate mandatory fields if orderType is 'order'
    if (orderType === 'order') {
      const missingFields = [];
      
      productCustomizations?.categories?.forEach(category => {
        category.options?.forEach(option => {
          if (option.is_required) {
            // Check if option is visible based on conditional logic
            if (!isOptionVisible(option)) {
              return; // Skip validation if option is not visible
            }
            
            const value = customizationValues[option.option_id];
            
            if (!value || value === '__none__' || value === '') {
              missingFields.push({
                categoryName: category.name,
                optionName: option.name
              });
            }
          }
        });
      });
      
      if (missingFields.length > 0) {
        const fieldsList = missingFields
          .map(f => `${f.categoryName} - ${f.optionName}`)
          .join('\n• ');
        
        toast.error(
          `Cannot add item in Order mode. Missing mandatory fields:\n• ${fieldsList}`,
          { duration: 5000 }
        );
        return;
      }
    }

    // Use custom base price if set, otherwise use product's base price
    const basePrice = customBasePrice !== null ? customBasePrice : selectedProduct.base_price;

    // Calculate price with customization impacts
    let totalPrice = basePrice;
    let customizationPriceImpact = 0;
    productCustomizations?.categories?.forEach(cat => {
      cat.options?.forEach(opt => {
        if (customizationValues[opt.option_id] && opt.price_impact) {
          totalPrice += opt.price_impact;
          customizationPriceImpact += opt.price_impact;
        }
      });
    });

    const lineItem = {
      id: editingLineItem?.id || Date.now().toString(),
      product_id: selectedProduct.product_id,
      product_name: selectedProduct.name,
      garment_type: selectedProduct.default_garment_type || selectedProduct.name.toLowerCase(),
      production_factory_id: editingLineItem?.production_factory_id ?? null,
      quantity: lineItemQuantity,
      base_price: basePrice,
      customization_price_impact: customizationPriceImpact,
      line_item_price: totalPrice,
      customizations: Object.entries(customizationValues).map(([optionId, value]) => {
        const option = productCustomizations?.categories
          ?.flatMap(c => c.options || [])
          ?.find(o => o.option_id === optionId);
        return {
          option_id: optionId,
          option_name: option?.name,
          category_name: productCustomizations?.categories?.find(c => 
            c.options?.some(o => o.option_id === optionId)
          )?.name,
          selected_value: value,
          price_impact: option?.price_impact || 0,
        };
      }).filter(c => c.selected_value),
      snapshot_customization_schema: productCustomizations, // Save the customization schema at creation time
    };

    if (editingLineItem) {
      // Update existing line item
      setLineItems(lineItems.map(item => item.id === editingLineItem.id ? lineItem : item));
      toast.success('Product updated');
    } else {
      // Add new line item
      setLineItems([...lineItems, lineItem]);
      toast.success('Product added to order');
    }
    
    setProductDialogOpen(false);
    setSelectedProduct(null);
    setProductCustomizations(null);
    setCustomizationValues({});
    setLineItemQuantity(1);
    setEditingLineItem(null);
    setCustomBasePrice(null);
  };

  // Handle editing a line item
  const handleEditLineItem = async (item) => {
    setEditingLineItem(item);
    setLineItemQuantity(item.quantity || 1);
    const product = products.find(p => p.product_id === item.product_id);
    if (product) {
      setSelectedProduct(product);
      // Set the custom base price from the existing line item
      setCustomBasePrice(item.base_price);
      
      // Fetch product customizations
      setLoading(true);
      try {
        if (item.snapshot_customization_schema) {
          setProductCustomizations(item.snapshot_customization_schema);
        } else {
          const response = await getProductWithCustomizations(product.product_id);
          setProductCustomizations(response.data);
        }
        
        // Pre-populate customization values from the line item
        const values = {};
        item.customizations?.forEach(c => {
          if (c.option_id) {
            values[c.option_id] = c.selected_value;
          }
        });
        setCustomizationValues(values);
        setProductDialogOpen(true);
      } catch (error) {
        toast.error('Failed to load customizations');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleRemoveLineItem = (itemId) => {
    setLineItems(lineItems.filter(item => item.id !== itemId));
  };

  const handleDuplicateLineItem = (item) => {
    const duplicatedItem = {
      ...item,
      id: `temp_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`,
      // Deep copy customizations
      customizations: item.customizations?.map(c => ({...c})) || [],
      snapshot_customization_schema: item.snapshot_customization_schema
        ? { ...item.snapshot_customization_schema }
        : item.snapshot_customization_schema,
    };
    setLineItems([...lineItems, duplicatedItem]);
    toast.success('Line item duplicated');
  };

  const handleLineItemFactoryChange = (lineItemId, factoryId) => {
    setLineItems((prev) =>
      prev.map((item) =>
        item.id === lineItemId
          ? {
              ...item,
              production_factory_id: factoryId,
            }
          : item
      )
    );
  };

  const handleAddTestGarmentLineItem = () => {
    if (selectedTestProduct === '__none__') {
      toast.error('Please select a Test Product');
      return;
    }

    const product = testProducts.find((p) => p.product_id === selectedTestProduct);
    if (!product) {
      toast.error('Selected test product not found');
      return;
    }

    const lineItem = {
      id: `test-${Date.now().toString()}`,
      product_id: product.product_id,
      product_name: product.name,
      garment_type: product.default_garment_type || product.name.toLowerCase(),
      is_test_garment: true,
      production_factory_id: testGarmentFactorySelection === '__none__' ? null : testGarmentFactorySelection,
      quantity: 1,
      base_price: 0,
      customization_price_impact: 0,
      line_item_price: 0,
      customizations: [],
      snapshot_customization_schema: null,
    };

    setLineItems((prev) => [...prev, lineItem]);
    setTestGarmentDialogOpen(false);
    setSelectedTestProduct('__none__');
    setTestGarmentFactorySelection('__none__');
    toast.success('Test garment added');
  };

  const handleOpenAddCustomField = (categoryId) => {
    setCustomFieldCategoryId(categoryId);
    setCustomFieldForm({ field_name: '', field_value: '' });
    setAddCustomFieldDialogOpen(true);
  };

  const handleConfirmAddCustomField = () => {
    if (!customFieldCategoryId || !customFieldForm.field_name.trim()) {
      toast.error('Please enter a field name');
      return;
    }

    const customFieldId = `custom-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

    const newOption = {
      option_id: customFieldId,
      name: customFieldForm.field_name.trim(),
      option_type: 'text',
      is_required: false,
      price_impact: 0,
      placeholder: '',
      is_custom: true,
      display_order: 999,
    };

    setProductCustomizations((prev) => {
      if (!prev?.categories) return prev;
      return {
        ...prev,
        categories: prev.categories.map((cat) => {
          if (cat.category_id !== customFieldCategoryId) return cat;
          return {
            ...cat,
            options: [...(cat.options || []), newOption],
          };
        }),
      };
    });

    setCustomizationValues((prev) => ({
      ...prev,
      [customFieldId]: customFieldForm.field_value.trim(),
    }));

    toast.success('Custom field added');
    setAddCustomFieldDialogOpen(false);
    setCustomFieldCategoryId(null);
    setCustomFieldForm({ field_name: '', field_value: '' });
  };

  const calculateSubtotal = () => {
    return lineItems.reduce((sum, item) => sum + (item.line_item_price * item.quantity), 0);
  };

  const calculateTotals = () => {
    const subtotal = calculateSubtotal();
    let discountAmount = 0;
    
    if (orderDetails.discount_type === 'percentage' && orderDetails.discount_value > 0) {
      discountAmount = subtotal * (orderDetails.discount_value / 100);
    } else if (orderDetails.discount_type === 'fixed' && orderDetails.discount_value > 0) {
      discountAmount = orderDetails.discount_value;
    }
    
    const afterDiscount = subtotal - discountAmount;
    const gstAmount = afterDiscount * (orderDetails.gst_percentage / 100);
    const orderValue = afterDiscount + gstAmount;
    
    return {
      subtotal: subtotal.toFixed(2),
      discountAmount: discountAmount.toFixed(2),
      gstAmount: gstAmount.toFixed(2),
      orderValue: orderValue.toFixed(2),
    };
  };

  const isOptionVisible = (option) => {
    return checkOptionVisibility(option, customizationValues);
  };

  const checkOptionVisibility = (option, values) => {
    // If no conditional logic, always show
    if (!option.conditional_on_option_id) return true;
    
    // Get parent value
    const parentValue = values[option.conditional_on_option_id];
    const operator = option.conditional_operator || 'equals';
    const expectedValues = option.conditional_on_values || [];
    
    // Evaluate based on operator
    switch (operator) {
      case 'equals':
        if (!parentValue) return false;
        if (expectedValues.length > 0) {
          return expectedValues.includes(parentValue);
        }
        return true;
      
      case 'not_equals':
        if (!parentValue) return expectedValues.length > 0;
        if (expectedValues.length > 0) {
          return !expectedValues.includes(parentValue);
        }
        return false;
      
      case 'is_empty':
        return !parentValue || parentValue.trim() === '';
      
      case 'is_not_empty':
        return parentValue && parentValue.trim() !== '';
      
      case 'contains':
        if (!parentValue) return false;
        if (expectedValues.length > 0) {
          return expectedValues.some(val => parentValue.toLowerCase().includes(val.toLowerCase()));
        }
        return false;
      
      case 'not_contains':
        // If parent is empty, hide the child
        if (!parentValue || parentValue.trim() === '') return false;
        if (expectedValues.length > 0) {
          return !expectedValues.some(val => parentValue.toLowerCase().includes(val.toLowerCase()));
        }
        return true;
      
      case 'greater_than':
        if (!parentValue || expectedValues.length === 0) return false;
        const numValue = parseFloat(parentValue);
        const threshold = parseFloat(expectedValues[0]);
        return !isNaN(numValue) && !isNaN(threshold) && numValue > threshold;
      
      case 'less_than':
        if (!parentValue || expectedValues.length === 0) return false;
        const numVal = parseFloat(parentValue);
        const limit = parseFloat(expectedValues[0]);
        return !isNaN(numVal) && !isNaN(limit) && numVal < limit;
      
      default:
        return true;
    }
  };

  const handleSubmit = async () => {
    if (submittingLockRef.current) {
      return;
    }
    submittingLockRef.current = true;
    setSubmitting(true);

    let created = false;
    try {
    if (!selectedCustomer) {
      toast.error('Please select a customer');
      return;
    }
    if (lineItems.length === 0) {
      toast.error('Please add at least one product');
      return;
    }
    
    // Determine final status based on orderType
    const finalStatus = orderType === 'enquiry' ? 'enquiry' : 'pending_payment';
    
    // Validate customizations only if orderType is 'order' (pending_payment)
    if (orderType === 'order') {
      const missingCustomizations = [];
      
      for (let i = 0; i < lineItems.length; i++) {
        const item = lineItems[i];
        const product = products.find(p => p.product_id === item.product_id);
        
        if (!product) continue;
        
        // Fetch product customizations if not already available
        let productCust = productCustomizations;
        if (!productCust || productCust.product_id !== product.product_id) {
          try {
            const response = await getProductWithCustomizations(product.product_id);
            productCust = response.data;
          } catch (error) {
            console.error('Failed to fetch customizations:', error);
            continue;
          }
        }
        
        // Check for missing mandatory fields
        productCust?.categories?.forEach(category => {
          category.options?.forEach(option => {
            if (option.is_required) {
              // Build customization values map from item.customizations
              const itemCustomizationValues = {};
              item.customizations?.forEach(c => {
                itemCustomizationValues[c.option_id] = c.selected_value;
              });
              
              // Check if option is visible using item's values
              if (!checkOptionVisibility(option, itemCustomizationValues)) {
                return; // Skip validation if option is not visible
              }
              
              const customization = item.customizations?.find(
                c => c.option_id === option.option_id
              );
              const value = customization?.selected_value;
              
              if (!value || value === '__none__') {
                missingCustomizations.push({
                  itemIndex: i,
                  itemName: item.product_name,
                  categoryName: category.name,
                  optionName: option.name
                });
              }
            }
          });
        });
      }
      
      if (missingCustomizations.length > 0) {
        // Group by item for better summary
        const itemSummary = missingCustomizations.reduce((acc, field) => {
          const key = `Item ${field.itemIndex + 1}: ${field.itemName}`;
          if (!acc[key]) acc[key] = [];
          acc[key].push(`${field.categoryName} - ${field.optionName}`);
          return acc;
        }, {});
        
        const uniqueItems = new Set(missingCustomizations.map(f => f.itemIndex)).size;
        const summary = `⚠️ ${missingCustomizations.length} mandatory field(s) missing across ${uniqueItems} line item(s)`;
        
        const details = Object.entries(itemSummary)
          .map(([item, fields]) => `${item}:\n  • ${fields.join('\n  • ')}`)
          .join('\n\n');
        
        const message = `${summary}\n\nMissing Details:\n${details}\n\nSave as Enquiry instead?`;
        
        const saveAsEnquiry = window.confirm(message);
        if (saveAsEnquiry) {
          // User chose to save as enquiry
          setOrderType('enquiry');
        } else {
          // User cancelled
          toast.error('Please complete mandatory fields or save as Enquiry');
          return;
        }
      }
    }
    
    // Validate order details mandatory fields
    const missingFields = [];
    if (!orderDetails.client_final_date) missingFields.push('Client Deadline');
    if (!orderDetails.internal_delivery_date) missingFields.push('Internal Deadline');
    if (!orderDetails.test_fit_date) missingFields.push('Test Fit Date');
    if (!orderDetails.store || orderDetails.store === '__none__') missingFields.push('Store');
    
    // Validate group order fields
    if (isGroupParent && !groupName.trim()) {
      missingFields.push('Group Name');
    }
    
    if (missingFields.length > 0) {
      toast.error(`Please fill in required fields: ${missingFields.join(', ')}`);
      return;
    }

      const orderData = {
        customer_id: selectedCustomer.customer_id,
        status: orderType === 'enquiry' ? 'enquiry' : 'pending_payment', // Set status based on orderType
        client_final_date: orderDetails.client_final_date || null,
        internal_delivery_date: orderDetails.internal_delivery_date || null,
        test_fit_date: orderDetails.test_fit_date || null,
        store: orderDetails.store === '__none__' ? null : orderDetails.store,
        notes: orderDetails.notes || null,
        gst_percentage: parseFloat(orderDetails.gst_percentage) || 0,
        discount_type: orderDetails.discount_type === '__none__' ? null : orderDetails.discount_type,
        discount_value: parseFloat(orderDetails.discount_value) || 0,
        // Group order fields
        is_group_parent: isGroupParent,
        group_name: isGroupParent ? groupName.trim() : null,
        parent_order_id: !isGroupParent && selectedParentOrderId !== '__none__' ? selectedParentOrderId : null,
        line_items: lineItems.map(item => ({
          product_id: item.product_id,
          product_name: item.product_name,
          garment_type: item.garment_type,
          is_test_garment: !!item.is_test_garment,
          production_factory_id: item.production_factory_id || null,
          dispatch_factory_id: item.production_factory_id || null,
          quantity: item.quantity,
          base_price: item.base_price,
          customization_price_impact: item.customization_price_impact || 0,
          line_item_price: item.line_item_price,
          customizations: item.customizations,
          snapshot_customization_schema: item.snapshot_customization_schema, // Include snapshot
        })),
      };

      const orderResponse = await createOrder(orderData);
      created = true;

      // Create deposit payment if specified
      if (depositAmount && parseFloat(depositAmount) > 0 && paymentMethod !== '__none__') {
        try {
          await createPayment({
            order_id: orderResponse.data.order_id,
            amount: parseFloat(depositAmount),
            payment_method: paymentMethod,
            notes: 'Initial deposit',
          });
        } catch (paymentError) {
          if (isSessionExpiredError(paymentError)) {
            triggerSessionExpired({ reason: 'Your session expired after the order was created. Log in again — do not click Create Order again.' });
            navigate(`/orders/${orderResponse.data.order_id}/edit`);
            return;
          }
          toast.error(paymentError.response?.data?.detail || 'Order was created but the deposit payment failed. Record the payment on the order.');
          navigate(`/orders/${orderResponse.data.order_id}/edit`);
          return;
        }
      }

      toast.success('Order created successfully');
      localStorage.removeItem(NEW_ORDER_DRAFT_KEY);
      // Navigate to the edit page of the newly created order
      navigate(`/orders/${orderResponse.data.order_id}/edit`);
    } catch (error) {
      if (isSessionExpiredError(error)) {
        triggerSessionExpired({ reason: 'Your session expired while creating this order. Log in again to continue from the same draft.' });
        return;
      }
      toast.error(error.response?.data?.detail || 'Failed to create order');
    } finally {
      if (!created) {
        submittingLockRef.current = false;
        setSubmitting(false);
      }
    }
  };

  const filteredCustomers = customers.filter(c =>
    c.customer_name.toLowerCase().includes(customerSearch.toLowerCase()) ||
    c.email?.toLowerCase().includes(customerSearch.toLowerCase()) ||
    c.phone_number?.includes(customerSearch)
  );

  const getFactoryLabel = (factoryId) => {
    if (!factoryId) return 'No factory';
    const matched = factories.find((factory) => factory.factory_id === factoryId);
    return matched ? `${matched.factory_name} (${matched.factory_code})` : factoryId;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
  };

  const totals = calculateTotals();

  const canProceed = () => {
    if (currentStep === 1) return selectedCustomer !== null;
    if (currentStep === 2) return lineItems.length > 0;
    return true;
  };

  return (
    <div className="space-y-6 p-3" data-testid="new-order-page">
      {sessionModalOpen ? (
        <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800" data-testid="new-order-session-expired-banner">
          Session expired. Use the popup login to continue without losing your order draft.
        </div>
      ) : null}
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/orders')}>
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="text-3xl font-semibold tracking-tight" style={{ fontFamily: 'Playfair Display, serif' }}>
            New Order
          </h1>
          <p className="text-muted-foreground">Create a new customer order</p>
        </div>
      </div>

      {/* Progress Steps */}
      <div className="flex items-center justify-center">
        <div className="flex items-center gap-2">
          {steps.map((step, index) => (
            <div key={step.id} className="flex items-center">
              <button
                onClick={() => step.id < currentStep && setCurrentStep(step.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-sm transition-colors ${
                  step.id === currentStep
                    ? 'bg-primary text-primary-foreground'
                    : step.id < currentStep
                    ? 'bg-primary/20 text-primary cursor-pointer hover:bg-primary/30'
                    : 'bg-secondary text-muted-foreground'
                }`}
                disabled={step.id > currentStep}
              >
                {step.id < currentStep ? (
                  <Check className="w-4 h-4" />
                ) : (
                  <step.icon className="w-4 h-4" />
                )}
                <span className="text-sm font-medium">{step.name}</span>
              </button>
              {index < steps.length - 1 && (
                <div className={`w-12 h-0.5 mx-2 ${step.id < currentStep ? 'bg-primary' : 'bg-secondary'}`} />
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Step Content */}
      <div className="max-w-4xl mx-auto">
        {/* Step 1: Customer Selection */}
        {currentStep === 1 && (
          <>
          <Card className="border-white/10 bg-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="w-5 h-5 text-primary" />
                Select Customer
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {selectedCustomer ? (
                <div className="p-4 rounded-sm bg-primary/10 border border-primary/20">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-lg">{selectedCustomer.customer_name}</p>
                      {selectedCustomer.email && <p className="text-sm text-muted-foreground">{selectedCustomer.email}</p>}
                      {selectedCustomer.phone_number && <p className="text-sm text-muted-foreground">{selectedCustomer.phone_number}</p>}
                    </div>
                    <Button variant="outline" onClick={() => setSelectedCustomer(null)}>
                      Change
                    </Button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        placeholder="Search customers..."
                        value={customerSearch}
                        onChange={(e) => setCustomerSearch(e.target.value)}
                        className="pl-10 h-11 bg-secondary/50"
                        data-testid="customer-search"
                      />
                    </div>
                    <Button variant="outline" onClick={() => setShowNewCustomer(true)}>
                      <Plus className="w-4 h-4 mr-2" />
                      New Customer
                    </Button>
                  </div>

                  <div className="max-h-[60vh] overflow-y-auto space-y-2">
                    <div className="pb-1 text-xs text-muted-foreground" data-testid="new-order-customer-count-label">
                      Showing {filteredCustomers.length} of {customers.length} clients
                    </div>
                    {filteredCustomers.map((customer) => (
                      <button
                        key={customer.customer_id}
                        onClick={() => setSelectedCustomer(customer)}
                        className="w-full p-3 rounded-sm bg-secondary/30 hover:bg-secondary/50 text-left transition-colors"
                        data-testid={`customer-option-${customer.customer_id}`}
                      >
                        <p className="font-medium">{customer.customer_name}</p>
                        <p className="text-sm text-muted-foreground">
                          {[customer.email, customer.phone_number].filter(Boolean).join(' • ')}
                        </p>
                      </button>
                    ))}
                    {filteredCustomers.length === 0 && (
                      <p className="text-center text-muted-foreground py-8">
                        No customers found. Create a new customer to continue.
                      </p>
                    )}
                  </div>
                </>
              )}

              {/* New Customer Dialog */}
              <Dialog open={showNewCustomer} onOpenChange={setShowNewCustomer}>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>New Customer</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label>Name *</Label>
                      <Input
                        value={newCustomerForm.customer_name}
                        onChange={(e) => setNewCustomerForm({ ...newCustomerForm, customer_name: e.target.value })}
                        placeholder="Customer name"
                        className="h-11 bg-secondary/50"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Email</Label>
                      <Input
                        type="email"
                        value={newCustomerForm.email}
                        onChange={(e) => setNewCustomerForm({ ...newCustomerForm, email: e.target.value })}
                        placeholder="Email address"
                        className="h-11 bg-secondary/50"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Phone</Label>
                      <Input
                        value={newCustomerForm.phone_number}
                        onChange={(e) => setNewCustomerForm({ ...newCustomerForm, phone_number: e.target.value })}
                        placeholder="Phone number"
                        className="h-11 bg-secondary/50"
                      />
                    </div>
                    <div className="flex justify-end gap-3">
                      <Button variant="outline" onClick={() => setShowNewCustomer(false)}>Cancel</Button>
                      <Button onClick={handleCreateCustomer} disabled={!newCustomerForm.customer_name}>
                        Create Customer
                      </Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            </CardContent>
          </Card>

          {/* Group Order Management - Only show after customer is selected */}
          {selectedCustomer && (
            <Card className="border-white/10 bg-card mt-6">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base">
                  <Users className="w-5 h-5 text-primary" />
                  Group Order Management
                </CardTitle>
                <CardDescription>
                  Manage group parent/child relationships
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Parent Order Checkbox */}
                <div className="flex items-center space-x-3">
                  <Checkbox
                    id="isGroupParent"
                    checked={isGroupParent}
                    onCheckedChange={(checked) => {
                      setIsGroupParent(checked);
                      if (checked) {
                        setSelectedParentOrderId('__none__');
                      } else {
                        setGroupName('');
                      }
                    }}
                  />
                  <Label 
                    htmlFor="isGroupParent" 
                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                  >
                    This is a Parent Order for a Group
                  </Label>
                </div>

                {/* Group Name - Show when is parent */}
                {isGroupParent && (
                  <div className="space-y-2 ml-7">
                    <Label htmlFor="groupName">Group Name *</Label>
                    <Input
                      id="groupName"
                      value={groupName}
                      onChange={(e) => setGroupName(e.target.value)}
                      placeholder="e.g., Smith Wedding Party"
                      className="h-10 bg-secondary/50"
                    />
                  </div>
                )}

                {/* Part of Group - Show when NOT a parent */}
                {!isGroupParent && (
                  <div className="space-y-2">
                    <Label>Part of Group (Optional)</Label>
                    <GroupOrderCombobox
                      options={parentOrders.map(order => ({
                        value: order.order_id,
                        label: order.group_name ? `${order.group_name} (${order.order_number})` : order.order_number,
                        metadata: `CN ${order.customer_no ?? '—'} — ${order.customer_name || '—'} — ${order.customer_phone || order.customer_email || 'No contact'} — ${order.created_date}`
                      }))}
                      value={selectedParentOrderId === '__none__' ? '' : selectedParentOrderId}
                      onValueChange={(val) => setSelectedParentOrderId(val || '__none__')}
                      onSearch={fetchParentOrders}
                      placeholder="Search group name, order no, customer no, name, phone, email..."
                      emptyText="No group orders available. Create a parent order first."
                      loading={parentOrdersLoading}
                      className="w-full"
                    />
                    <p className="text-xs text-muted-foreground">
                      {parentOrders.length > 0 
                        ? `Showing ${parentOrders.length} group order${parentOrders.length !== 1 ? 's' : ''}. Search by group name, order no, customer no, name, phone, or email.`
                        : 'No recent group orders. Search by group name/order no/customer no to find older groups or create a parent order first.'
                      }
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
          </>
        )}

        {/* Step 2: Products & Customization */}
        {currentStep === 2 && (
          <div className="space-y-6">
            {/* Order Type Toggle */}
            <Card className="border-slate-200 bg-slate-50">
              <CardContent className="pt-6">
                <div className="space-y-3">
                  <Label className="text-sm font-medium text-slate-700">Order Type</Label>
                  <div className="flex gap-4">
                    <button
                      type="button"
                      className={`flex-1 px-4 py-3 rounded-lg border-2 transition-all ${
                        orderType === 'enquiry'
                          ? 'bg-slate-600 border-slate-600 text-white shadow-md'
                          : 'bg-white border-slate-300 text-slate-700 hover:border-slate-400'
                      }`}
                      onClick={() => setOrderType('enquiry')}
                    >
                      <div className="flex flex-col items-center gap-1">
                        <span className="text-2xl">📝</span>
                        <span className="font-medium">Enquiry</span>
                        <span className="text-xs opacity-80">Quick capture</span>
                      </div>
                    </button>
                    <button
                      type="button"
                      className={`flex-1 px-4 py-3 rounded-lg border-2 transition-all ${
                        orderType === 'order'
                          ? 'bg-blue-600 border-blue-600 text-white shadow-md'
                          : 'bg-white border-slate-300 text-slate-700 hover:border-slate-400'
                      }`}
                      onClick={() => setOrderType('order')}
                    >
                      <div className="flex flex-col items-center gap-1">
                        <span className="text-2xl">📦</span>
                        <span className="font-medium">Order</span>
                        <span className="text-xs opacity-80">Full details required</span>
                      </div>
                    </button>
                  </div>
                  {orderType === 'enquiry' && (
                    <div className="flex items-start gap-2 p-3 bg-blue-50 border border-blue-200 rounded-md">
                      <span className="text-blue-600 text-sm">ℹ️</span>
                      <p className="text-xs text-blue-700">
                        Enquiry mode: Mandatory customization fields are optional. You can complete details later.
                      </p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Product Selection */}
            <Card className="border-white/10 bg-card">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <Package className="w-5 h-5 text-primary" />
                    Add Products
                  </CardTitle>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setTestGarmentDialogOpen(true)}
                      className="flex items-center gap-2"
                      data-testid="new-order-add-test-garment-button"
                    >
                      <Shirt className="w-4 h-4" />
                      Add Test Garment
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCopyDialogOpen(true)}
                      className="flex items-center gap-2"
                      data-testid="new-order-copy-from-order-button"
                    >
                      <ClipboardCopy className="w-4 h-4" />
                      Copy from Order
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                  {products.map((product) => (
                    <button
                      key={product.product_id}
                      onClick={() => handleSelectProduct(product)}
                      className="p-4 rounded-sm bg-secondary/30 hover:bg-secondary/50 border border-white/5 hover:border-primary/30 transition-all text-center"
                      data-testid={`product-${product.product_id}`}
                    >
                      <p className="font-medium">{product.name}</p>
                      <p className="text-sm text-primary mt-1">{formatCurrency(product.base_price)}</p>
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>

            <CopyFromOrderDialog
              open={copyDialogOpen}
              onOpenChange={setCopyDialogOpen}
              onConfirm={handleCopyFromOrder}
            />

            <Dialog open={testGarmentDialogOpen} onOpenChange={setTestGarmentDialogOpen}>
              <DialogContent className="sm:max-w-md" data-testid="new-order-test-garment-dialog">
                <DialogHeader>
                  <DialogTitle data-testid="new-order-test-garment-dialog-title">Add Test Garment</DialogTitle>
                  <DialogDescription data-testid="new-order-test-garment-dialog-description">
                    Create a test garment line item directly, without adding regular garments.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-2">
                  <div className="space-y-2">
                    <Label>Test Product *</Label>
                    <Select value={selectedTestProduct} onValueChange={setSelectedTestProduct}>
                      <SelectTrigger data-testid="new-order-test-garment-product-select-trigger">
                        <SelectValue placeholder="Select a test product" />
                      </SelectTrigger>
                      <SelectContent data-testid="new-order-test-garment-product-select-content">
                        <SelectItem value="__none__" data-testid="new-order-test-garment-product-option-none">Select test product...</SelectItem>
                        {testProducts.map((product) => (
                          <SelectItem
                            key={product.product_id}
                            value={product.product_id}
                            data-testid={`new-order-test-garment-product-option-${product.product_id}`}
                          >
                            {product.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Factory (Per Test Line Item)</Label>
                    <Select value={testGarmentFactorySelection} onValueChange={setTestGarmentFactorySelection}>
                      <SelectTrigger data-testid="new-order-test-garment-factory-select-trigger">
                        <SelectValue placeholder="Select factory" />
                      </SelectTrigger>
                      <SelectContent data-testid="new-order-test-garment-factory-select-content">
                        <SelectItem value="__none__" data-testid="new-order-test-garment-factory-option-none">No factory</SelectItem>
                        {factories.map((factory) => (
                          <SelectItem
                            key={factory.factory_id}
                            value={factory.factory_id}
                            data-testid={`new-order-test-garment-factory-option-${factory.factory_id}`}
                          >
                            {factory.factory_name} ({factory.factory_code})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <DialogFooter>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setTestGarmentDialogOpen(false);
                      setSelectedTestProduct('__none__');
                      setTestGarmentFactorySelection('__none__');
                    }}
                    data-testid="new-order-test-garment-cancel-button"
                  >
                    Cancel
                  </Button>
                  <Button onClick={handleAddTestGarmentLineItem} data-testid="new-order-test-garment-add-button">
                    Add Test Garment
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            {/* Order Cart */}
            <Card className="border-white/10 bg-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <ShoppingCart className="w-5 h-5 text-primary" />
                  Order Items ({lineItems.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {lineItems.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">
                    No products added yet. Select products above to add to the order.
                  </p>
                ) : (
                  <div className="space-y-3">
                    {lineItems.map((item, index) => (
                      <div key={item.id} className="p-4 rounded-sm bg-secondary/30 border border-white/5">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-muted-foreground">#{index + 1}</span>
                              <span className="font-medium">{item.product_name}</span>
                              <Badge variant="outline" className="text-xs">
                                {item.garment_type}
                              </Badge>
                              <Badge variant="secondary" className="text-xs" data-testid={`new-order-line-item-quantity-badge-${item.id}`}>
                                Qty {item.quantity}
                              </Badge>
                              {item.is_test_garment && (
                                <Badge variant="secondary" className="text-xs" data-testid={`new-order-line-item-test-badge-${item.id}`}>
                                  Test Garment
                                </Badge>
                              )}
                            </div>
                            {item.customizations.length > 0 && (
                              <div className="mt-2 space-y-1">
                                {item.customizations.slice(0, 3).map((c, i) => (
                                  <p key={i} className="text-xs text-muted-foreground">
                                    {c.option_name}: {c.selected_value}
                                    {c.price_impact > 0 && ` (+${formatCurrency(c.price_impact)})`}
                                  </p>
                                ))}
                                {item.customizations.length > 3 && (
                                  <p className="text-xs text-muted-foreground">
                                    +{item.customizations.length - 3} more customizations
                                  </p>
                                )}
                              </div>
                            )}
                            <p className="text-xs text-muted-foreground mt-2" data-testid={`new-order-line-item-factory-${item.id}`}>
                              Factory: {getFactoryLabel(item.production_factory_id)}
                            </p>
                            <div className="mt-2 max-w-xs">
                              <Select
                                value={item.production_factory_id || '__none__'}
                                onValueChange={(value) => handleLineItemFactoryChange(item.id, value === '__none__' ? null : value)}
                              >
                                <SelectTrigger
                                  className="h-8 text-xs"
                                  data-testid={`new-order-line-item-factory-select-trigger-${item.id}`}
                                >
                                  <SelectValue placeholder="No factory" />
                                </SelectTrigger>
                                <SelectContent data-testid={`new-order-line-item-factory-select-content-${item.id}`}>
                                  <SelectItem value="__none__" data-testid={`new-order-line-item-factory-option-none-${item.id}`}>
                                    No factory
                                  </SelectItem>
                                  {factories.map((factory) => (
                                    <SelectItem
                                      key={factory.factory_id}
                                      value={factory.factory_id}
                                      data-testid={`new-order-line-item-factory-option-${item.id}-${factory.factory_id}`}
                                    >
                                      {factory.factory_name} ({factory.factory_code})
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                          <div className="flex items-center gap-4">
                            <span className="font-medium">{formatCurrency(item.line_item_price)}</span>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => handleEditLineItem(item)}
                              title="Edit item"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"/></svg>
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => handleDuplicateLineItem(item)}
                              title="Duplicate item"
                            >
                              <Copy className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-destructive hover:text-destructive"
                              onClick={() => handleRemoveLineItem(item.id)}
                              title="Remove item"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                    
                    <div className="pt-4 border-t border-white/10">
                      <div className="flex justify-between text-lg font-medium">
                        <span>Subtotal</span>
                        <span className="text-primary">{formatCurrency(calculateSubtotal())}</span>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Product Customization Dialog */}
            <CustomizationFlowModal
              open={productDialogOpen}
              onOpenChange={setProductDialogOpen}
              title={`${editingLineItem ? 'Edit' : 'Customize'} ${selectedProduct?.name || ''}`}
              description={editingLineItem ? 'Update line-item customizations and base price.' : 'Configure this product using guided mode.'}
              loading={loading}
              categories={productCustomizations?.categories || []}
              values={customizationValues}
              setValues={setCustomizationValues}
              isOptionVisible={checkOptionVisibility}
              formatCurrency={formatCurrency}
              requireMandatory={orderType === 'order'}
              onSubmit={handleAddLineItem}
              submitLabel={editingLineItem ? 'Update Item' : 'Add to Order'}
              basePriceValue={customBasePrice !== null ? customBasePrice : (selectedProduct?.base_price || 0)}
              basePriceDefault={selectedProduct?.base_price || 0}
              onBasePriceChange={setCustomBasePrice}
              quantity={lineItemQuantity}
              onQuantityChange={setLineItemQuantity}
              showQuantity
              showAddCustomField
              onAddCustomField={handleOpenAddCustomField}
              onCloseCleanup={() => {
                setEditingLineItem(null);
                setSelectedProduct(null);
                setProductCustomizations(null);
                setCustomizationValues({});
                setLineItemQuantity(1);
                setCustomBasePrice(null);
                setAddCustomFieldDialogOpen(false);
                setCustomFieldCategoryId(null);
                setCustomFieldForm({ field_name: '', field_value: '' });
              }}
              dataTestIdPrefix="new-order-customization-flow"
              referencePanel={getFabricReferencePanel(lineItems, editingLineItem?.id)}
            />

            <Dialog open={addCustomFieldDialogOpen} onOpenChange={setAddCustomFieldDialogOpen}>
              <DialogContent className="sm:max-w-md" data-testid="new-order-add-custom-field-dialog">
                <DialogHeader>
                  <DialogTitle data-testid="new-order-add-custom-field-title">Add Custom Field</DialogTitle>
                  <DialogDescription data-testid="new-order-add-custom-field-description">
                    Add a custom text field to this line item. This field appears only on this line item.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4" data-testid="new-order-add-custom-field-form">
                  <div className="space-y-2">
                    <Label htmlFor="new_order_custom_field_name">Field Name *</Label>
                    <Input
                      id="new_order_custom_field_name"
                      value={customFieldForm.field_name}
                      onChange={(e) => setCustomFieldForm({
                        ...customFieldForm,
                        field_name: e.target.value,
                      })}
                      placeholder="e.g., Special Instructions"
                      className="h-10"
                      data-testid="new-order-custom-field-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="new_order_custom_field_value">Field Value</Label>
                    <Textarea
                      id="new_order_custom_field_value"
                      value={customFieldForm.field_value}
                      onChange={(e) => setCustomFieldForm({
                        ...customFieldForm,
                        field_value: e.target.value,
                      })}
                      placeholder="Enter the value for this field..."
                      className="min-h-[80px]"
                      rows={3}
                      data-testid="new-order-custom-field-value-input"
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setAddCustomFieldDialogOpen(false);
                      setCustomFieldForm({ field_name: '', field_value: '' });
                    }}
                    data-testid="new-order-add-custom-field-cancel-button"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleConfirmAddCustomField}
                    data-testid="new-order-add-custom-field-confirm-button"
                  >
                    Add Field
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        )}

        {/* Step 3: Review & Finalize */}
        {currentStep === 3 && (
          <div className="space-y-6">
            {/* Order Type Status (Can be changed in Step 3) */}
            <Card className="border-slate-200 bg-slate-50">
              <CardContent className="pt-6">
                <div className="space-y-3">
                  <Label className="text-sm font-medium text-slate-700">Order Type / Status</Label>
                  <div className="flex gap-4">
                    <button
                      type="button"
                      className={`flex-1 px-4 py-3 rounded-lg border-2 transition-all ${
                        orderType === 'enquiry'
                          ? 'bg-slate-600 border-slate-600 text-white shadow-md'
                          : 'bg-white border-slate-300 text-slate-700 hover:border-slate-400'
                      }`}
                      onClick={() => setOrderType('enquiry')}
                    >
                      <div className="flex flex-col items-center gap-1">
                        <span className="text-2xl">📝</span>
                        <span className="font-medium">Enquiry</span>
                      </div>
                    </button>
                    <button
                      type="button"
                      className={`flex-1 px-4 py-3 rounded-lg border-2 transition-all ${
                        orderType === 'order'
                          ? 'bg-blue-600 border-blue-600 text-white shadow-md'
                          : 'bg-white border-slate-300 text-slate-700 hover:border-slate-400'
                      }`}
                      onClick={() => setOrderType('order')}
                    >
                      <div className="flex flex-col items-center gap-1">
                        <span className="text-2xl">📦</span>
                        <span className="font-medium">Order</span>
                      </div>
                    </button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Order Summary */}
            <Card className="border-white/10 bg-card">
              <CardHeader>
                <CardTitle>Order Summary</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Customer Info */}
                <div className="p-4 rounded-sm bg-secondary/30">
                  <p className="text-sm text-muted-foreground mb-1">Customer</p>
                  <p className="font-medium">{selectedCustomer?.customer_name}</p>
                  {selectedCustomer?.email && <p className="text-sm text-muted-foreground">{selectedCustomer.email}</p>}
                </div>

                {/* Line Items */}
                <div>
                  <p className="text-sm text-muted-foreground mb-2">Items ({lineItems.length})</p>
                  <div className="space-y-2">
                    {lineItems.map((item, index) => (
                      <div key={item.id} className="flex justify-between items-center p-3 rounded-sm bg-secondary/20">
                        <div>
                          <span className="font-medium">{item.product_name}</span>
                          <span className="text-muted-foreground ml-2">× {item.quantity}</span>
                          {item.is_test_garment && (
                            <Badge variant="secondary" className="ml-2 text-xs" data-testid={`new-order-review-test-badge-${item.id}`}>
                              Test Garment
                            </Badge>
                          )}
                          <p className="text-xs text-muted-foreground mt-1" data-testid={`new-order-review-line-item-factory-${item.id}`}>
                            Factory: {getFactoryLabel(item.production_factory_id)}
                          </p>
                        </div>
                        <span>{formatCurrency(item.line_item_price)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Order Details */}
            <Card className="border-white/10 bg-card">
              <CardHeader>
                <CardTitle>Order Details</CardTitle>
                <p className="text-xs text-muted-foreground">Fields marked with <span className="text-red-500">*</span> are required</p>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Client Deadline <span className="text-red-500">*</span></Label>
                    <DateField
                      value={orderDetails.client_final_date}
                      onChange={(next) => setOrderDetails({ ...orderDetails, client_final_date: next })}
                      className="h-11 w-full bg-secondary/50"
                      testId="new-order-client-final-date-input"
                      allowClear={false}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Internal Deadline <span className="text-red-500">*</span></Label>
                    <DateField
                      value={orderDetails.internal_delivery_date}
                      onChange={(next) => setOrderDetails({ ...orderDetails, internal_delivery_date: next })}
                      className="h-11 w-full bg-secondary/50"
                      testId="new-order-internal-delivery-date-input"
                      allowClear={false}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Test Fit Date <span className="text-red-500">*</span></Label>
                    <DateField
                      value={orderDetails.test_fit_date}
                      onChange={(next) => setOrderDetails({ ...orderDetails, test_fit_date: next })}
                      className="h-11 w-full bg-secondary/50"
                      testId="new-order-test-fit-date-input"
                      allowClear={false}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Store <span className="text-red-500">*</span></Label>
                    <Select 
                      value={orderDetails.store} 
                      onValueChange={(value) => setOrderDetails({ ...orderDetails, store: value })}
                    >
                      <SelectTrigger className="h-11 bg-secondary/50">
                        <SelectValue placeholder="Select store" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">Select store...</SelectItem>
                        {stores.map((store) => (
                          <SelectItem key={store.store_id} value={store.store_id}>
                            {store.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Notes</Label>
                  <Textarea
                    value={orderDetails.notes}
                    onChange={(e) => setOrderDetails({ ...orderDetails, notes: e.target.value })}
                    placeholder="Order notes..."
                    className="bg-secondary/50"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Pricing */}
            <Card className="border-white/10 bg-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <DollarSign className="w-5 h-5 text-primary" />
                  Pricing
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>GST %</Label>
                    <Input
                      type="number"
                      value={orderDetails.gst_percentage}
                      onChange={(e) => setOrderDetails({ ...orderDetails, gst_percentage: parseFloat(e.target.value) || 0 })}
                      className="h-11 bg-secondary/50"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Discount Type</Label>
                    <Select 
                      value={orderDetails.discount_type} 
                      onValueChange={(value) => setOrderDetails({ ...orderDetails, discount_type: value })}
                    >
                      <SelectTrigger className="h-11 bg-secondary/50">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">No discount</SelectItem>
                        <SelectItem value="percentage">Percentage</SelectItem>
                        <SelectItem value="fixed">Fixed Amount</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Discount Value</Label>
                    <Input
                      type="number"
                      value={orderDetails.discount_value}
                      onChange={(e) => setOrderDetails({ ...orderDetails, discount_value: parseFloat(e.target.value) || 0 })}
                      className="h-11 bg-secondary/50"
                      disabled={orderDetails.discount_type === '__none__'}
                    />
                  </div>
                </div>

                {/* Totals */}
                <div className="pt-4 border-t border-white/10 space-y-2">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Subtotal</span>
                    <span>{formatCurrency(totals.subtotal)}</span>
                  </div>
                  {parseFloat(totals.discountAmount) > 0 && (
                    <div className="flex justify-between text-red-400">
                      <span>Discount</span>
                      <span>-{formatCurrency(totals.discountAmount)}</span>
                    </div>
                  )}
                  {parseFloat(totals.gstAmount) > 0 && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">GST ({orderDetails.gst_percentage}%)</span>
                      <span>{formatCurrency(totals.gstAmount)}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-xl font-semibold pt-2 border-t border-white/10">
                    <span>Total</span>
                    <span className="text-primary">{formatCurrency(totals.orderValue)}</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Deposit */}
            <Card className="border-white/10 bg-card">
              <CardHeader>
                <CardTitle>Deposit Payment (Optional)</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Deposit Amount</Label>
                    <Input
                      type="number"
                      value={depositAmount}
                      onChange={(e) => setDepositAmount(e.target.value)}
                      placeholder="0.00"
                      className="h-11 bg-secondary/50"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Payment Method</Label>
                    <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                      <SelectTrigger className="h-11 bg-secondary/50">
                        <SelectValue placeholder="Select method" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">Select...</SelectItem>
                        <SelectItem value="Cash">Cash</SelectItem>
                        <SelectItem value="Credit Card">Credit Card</SelectItem>
                        <SelectItem value="Bank Transfer">Bank Transfer</SelectItem>
                        <SelectItem value="Check">Check</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Navigation Buttons */}
        <div className="flex justify-between pt-6">
          <Button
            variant="outline"
            onClick={() => setCurrentStep(s => Math.max(1, s - 1))}
            disabled={currentStep === 1}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Previous
          </Button>
          
          {currentStep < 3 ? (
            <Button
              onClick={() => setCurrentStep(s => Math.min(3, s + 1))}
              disabled={!canProceed()}
              className="bg-primary text-primary-foreground hover:bg-primary/90"
            >
              Next
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          ) : (
            <Button
              type="button"
              onClick={handleSubmit}
              disabled={submitting}
              aria-busy={submitting}
              className="bg-primary text-primary-foreground hover:bg-primary/90 gold-glow"
              data-testid="create-order-button"
            >
              {submitting ? (
                <span className="flex items-center gap-2">
                  <span className="animate-spin w-4 h-4 border-2 border-current border-t-transparent rounded-full" />
                  Creating...
                </span>
              ) : (
                <>
                  <Check className="w-4 h-4 mr-2" />
                  Create Order
                </>
              )}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default NewOrderPage;
